import React, { useState, useMemo } from "react";
import { projects } from "./data/projects";
import {
  calculateCategories,
  calculateStats,
} from "./utils/projectCalculations";
import { filterProjects } from "./utils/projectFilters";
import { useLoadingState } from "./hooks/useLoadingState";
import { ProjectsRenderer } from "./components/ProjectsRenderer";
import { getCategoryIcon } from "./utils/categoryIcons";
import { Globe, Star } from "lucide-react";
import "./styles/global.css";
import { HyperEcoMono } from "./components/HyperEcoMono";

const App: React.FC = () => {
  // State
  const [currentView, setCurrentView] = useState<"grid" | "list">("grid");
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Memoized calculations
  const categories = useMemo(() => calculateCategories(projects), []);
  const stats = useMemo(
    () => calculateStats(projects, categories),
    [categories]
  );
  const filteredProjects = useMemo(
    () => filterProjects(projects, activeFilter, searchTerm),
    [activeFilter, searchTerm]
  );

  // Loading state
  const isLoading = useLoadingState(searchTerm, activeFilter);

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif',
        background: "#000",
        color: "#fff",
        lineHeight: "1.5",
        overflowX: "hidden",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo flex items-center gap-2">
            <HyperEcoMono />
          </div>
          <div className="header-stats">
            <div className="stat">
              <span className="stat-number">{stats.totalProjects}</span>
              <span>Projects</span>
            </div>
            <div className="stat">
              <span className="stat-number">{stats.nativeProjects}</span>
              <span>Native</span>
            </div>
            <div className="stat">
              <span className="stat-number">{stats.pointsProjects}</span>
              <span>Points</span>
            </div>
            <div className="stat">
              <span className="stat-number">{stats.categories}</span>
              <span>Categories</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="main">
        <div className="container">
          {/* Hero */}
          <section className="hero">
            <h1>Hyperliquid</h1>
            <p className="hero-subtitle">
              Discover the next generation of decentralized finance, built on
              the HyperEVM.
            </p>
          </section>

          {/* Overview */}
          <section className="overview">
            <div className="overview-grid">
              <div className="overview-item">
                <div className="overview-number">{stats.totalProjects}</div>
                <div className="overview-label">Total Projects</div>
              </div>
              <div className="overview-item">
                <div className="overview-number">{stats.nativeProjects}</div>
                <div className="overview-label">Native Apps</div>
              </div>
              <div className="overview-item">
                <div className="overview-number">{stats.pointsProjects}</div>
                <div className="overview-label">Projects with Points</div>
              </div>
              <div className="overview-item">
                <div className="overview-number">{stats.categories}</div>
                <div className="overview-label">Categories</div>
              </div>
              <div className="overview-item">
                <div className="overview-number">{stats.liveProjects}</div>
                <div className="overview-label">Live Projects</div>
              </div>
            </div>
          </section>

          {/* Controls */}
          <section className="controls">
            <div className="search-section">
              <input
                type="text"
                className="search-input"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filters">
              <button
                className={`filter-btn ${
                  activeFilter === "all" ? "active" : ""
                }`}
                onClick={() => setActiveFilter("all")}
              >
                <Globe className="filter-icon" />
                All
              </button>
              <button
                className={`filter-btn points-filter ${
                  activeFilter === "points" ? "active" : ""
                }`}
                onClick={() => setActiveFilter("points")}
              >
                <Star className="filter-icon" />
                Points
              </button>
              {categories.map((category) => {
                const IconComponent = getCategoryIcon(category);
                return (
                  <button
                    key={category}
                    className={`filter-btn ${
                      activeFilter === category ? "active" : ""
                    }`}
                    onClick={() => setActiveFilter(category)}
                  >
                    <IconComponent className="filter-icon" />
                    {category}
                  </button>
                );
              })}
            </div>
          </section>

          {/* View Controls */}
          <div className="view-controls">
            <div className="results-count">
              {filteredProjects.length} project
              {filteredProjects.length !== 1 ? "s" : ""}
            </div>
            <div className="view-toggle">
              <button
                className={`view-btn ${currentView === "grid" ? "active" : ""}`}
                onClick={() => setCurrentView("grid")}
              >
                Grid
              </button>
              <button
                className={`view-btn ${currentView === "list" ? "active" : ""}`}
                onClick={() => setCurrentView("list")}
              >
                List
              </button>
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading projects...</p>
            </div>
          )}

          {/* Projects */}
          <div
            style={{
              opacity: isLoading ? 0.3 : 1,
              transition: "opacity 0.2s ease",
            }}
          >
            <ProjectsRenderer
              projects={filteredProjects}
              activeFilter={activeFilter}
              currentView={currentView}
            />
          </div>
        </div>
        <div className="footer">
          <p>
            To list your project, contact{" "}
            <a
              href="https://x.com/hyperfrenHL"
              target="_blank"
              rel="noopener noreferrer"
            >
              @Hyperfren.hl
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default App;
